import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Archive as ArchiveIcon, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from './ImpactDashboardTab';

export default function ImpactPrograms({ API_URL, getAuthHeader, refreshKey }) {
  const [programs, setPrograms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [editing, setEditing] = useState(null);

  const fetchPrograms = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/impact/programs`, { headers: getAuthHeader() });
      if (r.ok) setPrograms(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  useEffect(() => { fetchPrograms(); }, [fetchPrograms, refreshKey]);

  const createProgram = async () => {
    if (!form.name.trim()) return toast.error('Program name is required');
    const r = await fetch(`${API_URL}/api/impact/programs`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(form)
    });
    if (r.ok) { toast.success('Program created'); setShowForm(false); setForm({ name: '', description: '' }); fetchPrograms(); }
    else toast.error('Could not create program');
  };

  const patchProgram = async (key, body, msg) => {
    const r = await fetch(`${API_URL}/api/impact/programs/${key}`, {
      method: 'PATCH', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (r.ok) { toast.success(msg); setEditing(null); fetchPrograms(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Update failed'); }
  };

  const active = programs.filter(p => p.status === 'active');
  const archived = programs.filter(p => p.status === 'archived');

  const Card = ({ p }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`impact-program-${p.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full shrink-0" style={{ background: p.color || BRAND.teal }} />
          {editing === p.key ? (
            <input defaultValue={p.name} autoFocus className="border border-slate-300 rounded px-2 py-1 text-sm font-semibold w-64"
              data-testid={`impact-program-rename-input-${p.key}`}
              onKeyDown={e => { if (e.key === 'Enter') patchProgram(p.key, { name: e.target.value }, 'Program renamed'); if (e.key === 'Escape') setEditing(null); }}
              onBlur={e => { if (e.target.value !== p.name) patchProgram(p.key, { name: e.target.value }, 'Program renamed'); else setEditing(null); }} />
          ) : (
            <h3 className="font-bold text-sm" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>
              {p.name}
              {p.parent_key && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Sub-initiative</span>}
            </h3>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={() => setEditing(editing === p.key ? null : p.key)} title="Rename" data-testid={`impact-program-rename-${p.key}`}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><Pencil className="w-3.5 h-3.5" /></button>
          {p.key !== 'dcelc_wide' && (p.status === 'active' ? (
            <button onClick={() => patchProgram(p.key, { status: 'archived' }, 'Program archived — historical records preserved')}
              title="Archive" data-testid={`impact-program-archive-${p.key}`}
              className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50"><ArchiveIcon className="w-3.5 h-3.5" /></button>
          ) : (
            <button onClick={() => patchProgram(p.key, { status: 'active' }, 'Program restored')}
              title="Restore" data-testid={`impact-program-restore-${p.key}`}
              className="p-1.5 rounded-lg text-slate-400 hover:text-green-600 hover:bg-green-50"><RotateCcw className="w-3.5 h-3.5" /></button>
          ))}
        </div>
      </div>
      {p.description && <p className="text-xs text-slate-500 mt-2 leading-relaxed">{p.description}</p>}
      <div className="flex gap-4 mt-4 text-xs">
        <span><strong style={{ color: BRAND.navy }}>{p.stats?.unique_people ?? 0}</strong> people</span>
        <span><strong style={{ color: BRAND.navy }}>{p.stats?.participations ?? 0}</strong> participations</span>
        <span><strong style={{ color: BRAND.navy }}>{p.stats?.events ?? 0}</strong> events</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="impact-programs">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Programs classify every training and activity. Archiving preserves all historical records.</p>
        <button onClick={() => setShowForm(f => !f)} data-testid="impact-add-program-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: BRAND.teal }}>
          <Plus className="w-4 h-4" /> Add program
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="impact-program-form">
          <input placeholder="Program name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            data-testid="impact-program-name-input" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <textarea placeholder="Short description (optional)" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            data-testid="impact-program-desc-input" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" rows={2} />
          <div className="flex gap-2">
            <button onClick={createProgram} data-testid="impact-program-save-btn" className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: BRAND.navy }}>Create program</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-500 border border-slate-200">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">{active.map(p => <Card key={p.key} p={p} />)}</div>

      {archived.length > 0 && (
        <div>
          <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Archived programs (records preserved)</h4>
          <div className="grid md:grid-cols-2 gap-4 opacity-70">{archived.map(p => <Card key={p.key} p={p} />)}</div>
        </div>
      )}
    </div>
  );
}
